import { createTheme } from '@mui/material/styles';

// Enhanced theme that extends the existing theme with modern polish
// This file adds glassmorphism, better spacing, and modern visual effects
// while preserving all existing functionality and component behavior

// Modern color palette extensions
const colorExtensions = {
  // Enhanced primary with more shades
  primary: {
    50: '#e3f2fd',
    100: '#bbdefb', 
    200: '#90caf9',
    300: '#64b5f6',
    400: '#42a5f5',
    500: '#1976d2', // Keep existing main
    600: '#1565c0',
    700: '#0d47a1',
    800: '#0d47a1',
    900: '#0d3c78',
    main: '#1976d2', // Preserve existing
  },
  // Glassmorphism glass effects
  glass: {
    primary: 'rgba(25, 118, 210, 0.1)',
    secondary: 'rgba(220, 0, 78, 0.1)',
    white: 'rgba(255, 255, 255, 0.9)',
    light: 'rgba(255, 255, 255, 0.7)',
    dark: 'rgba(0, 0, 0, 0.1)',
  },
  // Enhanced gradients
  gradients: {
    primary: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
    secondary: 'linear-gradient(135deg, #dc004e 0%, #ad0e4e 100%)',
    brand: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
    glass: 'linear-gradient(145deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
    subtle: 'linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.95) 100%)',
  }
};

// Enhanced shadows for glassmorphism
const enhancedShadows = [
  'none',
  '0 2px 8px rgba(0, 0, 0, 0.04)', // elevation 1
  '0 4px 12px rgba(0, 0, 0, 0.06)', // elevation 2
  '0 6px 16px rgba(0, 0, 0, 0.08)', // elevation 3
  '0 8px 20px rgba(0, 0, 0, 0.10)', // elevation 4 - existing card shadow
  '0 10px 24px rgba(0, 0, 0, 0.12)', // elevation 5
  '0 12px 28px rgba(0, 0, 0, 0.14)', // elevation 6
  '0 16px 32px rgba(0, 0, 0, 0.16)', // elevation 7
  '0 20px 36px rgba(0, 0, 0, 0.18)', // elevation 8
  '0 24px 40px rgba(0, 0, 0, 0.20)', // elevation 9
  '0 28px 44px rgba(0, 0, 0, 0.22)', // elevation 10
  // Glass shadows for glassmorphism
  '0 8px 32px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(255, 255, 255, 0.1)', // glass light
  '0 12px 36px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(255, 255, 255, 0.15)', // glass medium
  '0 16px 40px rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(255, 255, 255, 0.2)', // glass strong
];

export const createEnhancedTheme = (existingTheme, mode = 'light') => {
  const dark = mode === 'dark';

  // Mode-aware glass palette
  const modeGlass = {
    primary: 'rgba(25, 118, 210, 0.1)',
    secondary: 'rgba(220, 0, 78, 0.1)',
    white: dark ? 'rgba(255,255,255,0.06)' : 'rgba(255, 255, 255, 0.9)',
    light: dark ? 'rgba(255,255,255,0.04)' : 'rgba(255, 255, 255, 0.7)',
    dark:  dark ? 'rgba(0,0,0,0.4)'        : 'rgba(0, 0, 0, 0.1)',
  };

  return createTheme({
    // Preserve all existing theme properties
    ...existingTheme,

    // Enhanced spacing (8px grid system)
    spacing: (factor) => `${8 * factor}px`,

    // Enhanced shape with more rounded corners
    shape: {
      ...existingTheme.shape,
      borderRadius: 12, // Slightly more rounded than existing 8px
    },

    // Enhanced palette
    palette: {
      ...existingTheme.palette,
      // Extend primary with more shades while keeping main
      primary: {
        ...existingTheme.palette.primary,
        ...colorExtensions.primary,
      },
      // Add glass and gradient colors
      glass: modeGlass,
      gradients: colorExtensions.gradients,
      // Creator (influencer) portal accent colors
      creator: {
        coral: '#FF6B6B',
        amber: '#FFB300',
        purple: '#7b1fa2',
        coralGlass: 'rgba(255, 107, 107, 0.12)',
        amberGlass: 'rgba(255, 179, 0, 0.12)',
        gradient: 'linear-gradient(135deg, #4a148c 0%, #7b1fa2 50%, #c2185b 100%)',
        gradientSubtle: 'linear-gradient(135deg, #fdf4ff 0%, #f3e5f5 40%, #fce4ec 100%)',
      },
      // Enhanced background for better glassmorphism
      background: {
        ...existingTheme.palette.background,
        glass: 'rgba(255, 255, 255, 0.9)',
        gradient: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      }
    },
    
    // Enhanced typography with Inter font as primary, Roboto as fallback
    typography: {
      ...existingTheme.typography,
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      // Enhanced font weights and sizes
      h1: {
        ...existingTheme.typography.h1,
        fontWeight: 700,
        letterSpacing: '-0.025em',
      },
      h2: {
        ...existingTheme.typography.h2,
        fontWeight: 600,
        letterSpacing: '-0.02em',
      },
      h3: {
        ...existingTheme.typography.h3,
        fontWeight: 600,
        letterSpacing: '-0.015em',
      },
      h4: {
        ...existingTheme.typography.h4,
        fontWeight: 600,
        letterSpacing: '-0.01em',
      },
      h5: {
        ...existingTheme.typography.h5,
        fontWeight: 600,
        letterSpacing: '-0.005em',
      },
      h6: {
        ...existingTheme.typography.h6,
        fontWeight: 600,
      },
      subtitle1: {
        ...existingTheme.typography.subtitle1,
        fontWeight: 500,
        lineHeight: 1.6,
      },
      body1: {
        ...existingTheme.typography.body1,
        lineHeight: 1.6,
      },
      body2: {
        ...existingTheme.typography.body2,
        lineHeight: 1.5,
      },
      button: {
        ...existingTheme.typography.button,
        fontWeight: 500,
        letterSpacing: '0.01em',
      },
    },
    
    // Enhanced shadows
    shadows: enhancedShadows,
    
    // Enhanced component overrides
    components: {
      ...existingTheme.components,
      
      // Enhanced MuiCard with glassmorphism
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            // Keep existing styles
            ...existingTheme.components?.MuiCard?.styleOverrides?.root,
            // Add glassmorphism enhancements
            background: dark
              ? 'rgba(30,35,50,0.95)'
              : (theme.palette.glass?.white || 'rgba(255, 255, 255, 0.9)'),
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 36px rgba(0, 0, 0, 0.12)',
            }
          }),
        },
      },
      
      // Enhanced MuiButton with better interactions
      MuiButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            // Keep existing styles
            ...existingTheme.components?.MuiButton?.styleOverrides?.root,
            // Enhanced transitions
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              transform: 'translateY(-1px)',
            },
            '&:active': {
              transform: 'translateY(0)',
            }
          }),
          contained: ({ theme }) => ({
            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
            '&:hover': {
              boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
            }
          }),
        },
      },
      
      // Enhanced MuiChip with modern styling
      MuiChip: {
        styleOverrides: {
          root: ({ theme }) => ({
            // Keep existing styles
            ...existingTheme.components?.MuiChip?.styleOverrides?.root,
            // Enhanced styling
            background: theme.palette.glass?.light || 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            transition: 'all 0.2s ease',
            '&:hover': {
              background: theme.palette.glass?.white || 'rgba(255, 255, 255, 0.9)',
              transform: 'translateY(-1px)',
            },
            '&.MuiChip-filled': {
              background: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': {
                background: theme.palette.primary.dark,
              }
            }
          }),
        },
      },
      
      // Enhanced MuiTextField with modern styling
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              background: dark ? 'rgba(255,255,255,0.06)' : 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(8px)',
              transition: 'all 0.2s ease',
              '&:hover': {
                background: dark ? 'rgba(255,255,255,0.09)' : 'rgba(255, 255, 255, 0.9)',
              },
              '&.Mui-focused': {
                background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(255, 255, 255, 0.95)',
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.15)',
              }
            }
          }
        }
      },
      
      // Enhanced MuiPaper for modern surfaces
      MuiPaper: {
        styleOverrides: {
          root: ({ theme }) => ({
            background: theme.palette.glass?.white || 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }),
        },
      },
    },
  });
};

// Export default enhanced theme creator
export default createEnhancedTheme;